/**
 * Recently viewed products for the empty basket drawer
 * (snippets/mini-cart-empty.liquid).
 *
 * Product pages add their handle to a list in this browser's localStorage
 * (window.comfyRecentlyViewedHandle, set in layout/theme.liquid). Any
 * [data-recently-viewed] row is filled from /products/<handle>.js and gets
 * arrows and, on mobile, page dots. The row stays hidden with no history.
 * Products are only fetched once the empty basket is on screen (basket page,
 * or the drawer opened), not on every page load.
 */
(() => {
  const STORAGE_KEY = 'comfyRecentlyViewed';
  const MAX_ITEMS = 12;
  const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

  function readHandles() {
    try {
      const list = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  function remember(handle) {
    if (!handle) return;
    const list = [handle, ...readHandles().filter((item) => item !== handle)].slice(0, MAX_ITEMS);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      // Private mode or storage blocked: nothing to remember
    }
  }

  let productsPromise = null;

  /** @returns {Promise<object[]>} Viewed products, newest first */
  function loadProducts() {
    if (productsPromise) return productsPromise;
    const root = window.Shopify?.routes?.root || '/';
    productsPromise = Promise.all(
      readHandles().map((handle) =>
        fetch(`${root}products/${encodeURIComponent(handle)}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    ).then((products) => products.filter((product) => product && product.available !== false));
    return productsPromise;
  }

  function sizedImage(src, width) {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  function cardHtml(product) {
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0] || {};
    const price = variant.price ?? product.price;
    const compare = variant.compare_at_price ?? product.compare_at_price;
    const image = product.featured_image || product.images?.[0];
    return `
      <a class="mini-cart-empty__card" href="${product.url}">
        <span class="mini-cart-empty__card-image">
          ${image ? `<img src="${sizedImage(image, 400)}" alt="${escapeHtml(product.title)}" loading="lazy" width="400" height="400">` : ''}
        </span>
        <span class="mini-cart-empty__card-title">${escapeHtml(product.title)}</span>
        <span class="mini-cart-empty__card-price">
          <strong>${money.format(price / 100)}</strong>
          ${compare > price ? `<s>${money.format(compare / 100)}</s>` : ''}
        </span>
      </a>`;
  }

  /** @param {HTMLElement} row */
  function setUpCarousel(row) {
    const track = row.querySelector('[data-recent-track]');
    const dots = row.querySelector('[data-recent-dots]');
    const step = () => {
      const card = track.querySelector('.mini-cart-empty__card');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : track.clientWidth;
    };
    const pages = () => Math.max(1, Math.ceil((track.scrollWidth - 1) / track.clientWidth));

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      row.querySelectorAll('[data-recent-prev]').forEach((button) => (button.disabled = track.scrollLeft <= 1));
      row.querySelectorAll('[data-recent-next]').forEach((button) => (button.disabled = track.scrollLeft >= max - 1));
      const count = pages();
      if (dots.children.length !== count) {
        dots.innerHTML = Array.from({ length: count }, () => '<span class="mini-cart-empty__dot"></span>').join('');
      }
      const active = max > 0 ? Math.round((track.scrollLeft / max) * (count - 1)) : 0;
      [...dots.children].forEach((dot, index) => dot.classList.toggle('is-active', index === active));
    };

    row.querySelectorAll('[data-recent-prev]').forEach((button) =>
      button.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }))
    );
    row.querySelectorAll('[data-recent-next]').forEach((button) =>
      button.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }))
    );
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
    // The drawer may still be closed (no width yet): settle once it shows
    new ResizeObserver(update).observe(track);
  }

  /** @param {HTMLElement} row */
  function isShown(row) {
    const box = row.closest('[data-cart-empty]') || row.parentElement;
    if (!box) return false;
    if (typeof box.checkVisibility === 'function') {
      return box.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true });
    }
    return box.getClientRects().length > 0 && getComputedStyle(box).visibility !== 'hidden';
  }

  /** @param {HTMLElement} row */
  function fill(row) {
    if (row.dataset.recentReady || !isShown(row)) return;
    row.dataset.recentReady = 'true';
    loadProducts().then((products) => {
      if (!products.length) return;
      row.querySelector('[data-recent-track]').innerHTML = products.map(cardHtml).join('');
      row.hidden = false;
      setUpCarousel(row);
    });
  }

  function fillAll() {
    if (!readHandles().length) return;
    document.querySelectorAll('[data-recently-viewed]').forEach(fill);
  }

  remember(window.comfyRecentlyViewedHandle);
  fillAll();

  // The drawer is a popover: fill it when it opens
  document.addEventListener(
    'toggle',
    (event) => {
      if (event.newState === 'open') requestAnimationFrame(fillAll);
    },
    true
  );
  // Browsers without popover open the drawer with a class instead
  const drawer = document.getElementById('mini-cart-wrapper');
  if (drawer) {
    new MutationObserver(() => {
      if (drawer.classList.contains('mini-cart-wrapper--fallback-open')) requestAnimationFrame(fillAll);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // The drawer re-renders its contents (e.g. when the basket empties)
  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => [...mutation.addedNodes].some((node) => node.nodeType === 1 && (node.matches('[data-recently-viewed]') || node.querySelector('[data-recently-viewed]'))))) {
      fillAll();
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
